import React, { useEffect, useState, useMemo } from "react";
import { Table } from "antd";
import Toolbar from "./_components/Toolbar";
import PaginationTable from "./_components/Pagination";
import "./style.scss";
import { useTranslation } from "react-i18next";

const DataTable = ({
  loading,
  columns,
  data,
  pagination,
  filters,
  sorter,
  hasMore,
  handleTableChange,
  onSearch,
  onInsert,
  refetch,
  extraButtons = [],
  rowKey,
  onSelectedRows,
  isFilterByDates = false,
  showFilter = true,
  components = {},
  startDate,
  endDate,
  showSelection = true,
  columnStorageKey,
  sortOptions,
  showColumnSetting = true,
  showSort = true,
}) => {
  const { t: translation } = useTranslation();
  const columnKeys = useMemo(() => columns.map((col) => col.dataIndex || col.key).filter(Boolean), [columns]);
  const getVisibleCols = () => {
    if (columnStorageKey) {
      try {
        const storedCols = JSON.parse(window.localStorage.getItem(columnStorageKey));
        const validStoredCols = Array.isArray(storedCols)
          ? storedCols.filter((key) => columnKeys.includes(key))
          : [];
        if (validStoredCols.length > 0) {
          return validStoredCols;
        }
      } catch {
        return columnKeys;
      }
    }
    return columnKeys;
  };
  const [visibleCols, setVisibleColsState] = useState(getVisibleCols);
  const setVisibleCols = (value) => {
    setVisibleColsState(value);
    if (columnStorageKey) {
      window.localStorage.setItem(columnStorageKey, JSON.stringify(value));
    }
  };
  useEffect(() => {
    setVisibleColsState(getVisibleCols());
  }, [columnStorageKey, columnKeys.join("|")]);

  const shownColumns = useMemo(
    () =>
      columns
        .filter((col) => visibleCols.includes(col.dataIndex || col.key))
        .map((col) => ({ ...col, sorter: false })),
    [columns, visibleCols]
  );

  const handleChangePagination = (page) => {
    handleTableChange(
      { ...pagination, current: page },
      { ...filters },
      { ...sorter }
    );
  };

  const handleChangeLimit = (limit) => {
    handleTableChange(
      { ...pagination, pageSize: limit, current: 1 },
      { ...filters },
      { ...sorter }
    );
  };

  const handleChangeFilter = (newFilters) => {
    handleTableChange({ ...pagination, current: 1 }, { ...newFilters }, { ...sorter });
  };

  const handleChangeSort = (newSorter) => {
    handleTableChange({ ...pagination }, { ...filters }, { ...newSorter });
  };

  const handleChangeFilterByDate = (dates) => {
    handleTableChange({ ...pagination }, { ...filters }, { ...sorter }, dates);
  };

  const rowSelectionConfig = {
    onChange: (selectedRowKeys, selectedRows) => {
      onSelectedRows(selectedRowKeys, selectedRows);
    },
    preserveSelectedRowKeys: true,
  };

  return (
    <div className="data-table">
      <Toolbar
        translation={translation}
        columns={columns}
        visibleCols={visibleCols}
        setVisibleCols={setVisibleCols}
        onSearch={onSearch}
        onInsert={onInsert}
        refetch={refetch}
        onFilter={handleChangeFilter}
        onSort={handleChangeSort}
        filters={filters}
        sorter={sorter}
        onFilterByDate={isFilterByDates ? handleChangeFilterByDate : undefined}
        showFilter={showFilter}
        extraButtons={extraButtons}
        loading={loading}
        startDate={startDate}
        endDate={endDate}
        sortOptions={sortOptions}
        showColumnSetting={showColumnSetting}
        showSort={showSort}
      />
      <Table
        key={new Date().getTime()}
        rowKey={rowKey}
        columns={shownColumns}
        dataSource={data}
        bordered={false}
        pagination={false}
        rowClassName={() => "custom-row"}
        scroll={{ x: "max-content" }}
        loading={loading}
        rowSelection={showSelection ? rowSelectionConfig : undefined}
        components={components}
      />
      <PaginationTable
        hasMore={hasMore}
        handlePaginations={handleChangePagination}
        limit={pagination.pageSize}
        handleLimit={handleChangeLimit}
        page={pagination.current}
      />
    </div>
  );
};

export default DataTable;
